"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { ModalContainer, MODAL_IDS } from "@wtc/ui";
import { UnifiedModal } from "../UnifiedModal";
import { toast } from "sonner";

// Mounted globally (app/layout.tsx → DetailPageModals), OUTSIDE any
// DetailActionProvider. All inputs (dealId + the bound `onConfirm` creator)
// arrive through the global-modal-stack data payload supplied by the deal
// `make-project-channel` action handler. See entity-actions-misc.ts. Enforced
// by scripts/validate-global-modal-context.mjs + validate-action-modal-mounts.mjs.

export interface MakeProjectChannelModalData {
  dealId: string;
  dealTitle?: string;
  onConfirm: (
    slug: string,
  ) => Promise<{ ok: boolean; error?: string; alreadyExisted?: boolean }>;
}

export function MakeProjectChannelModal() {
  return (
    <ModalContainer<MakeProjectChannelModalData> modalId={MODAL_IDS.deals.makeProjectChannel}>
      {({ data, onClose }) => <MakeProjectChannelModalContent data={data} onClose={onClose} />}
    </ModalContainer>
  );
}

function MakeProjectChannelModalContent({
  data,
  onClose,
}: {
  data?: MakeProjectChannelModalData;
  onClose: () => void;
}) {
  const [slug, setSlug] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = React.useCallback(async () => {
    const onConfirm = data?.onConfirm;
    if (!onConfirm) {
      toast.error("Unable to create the project channel — no handler available");
      return;
    }
    if (!slug.trim()) {
      toast.error("Enter a project slug");
      return;
    }
    setBusy(true);
    try {
      const res = await onConfirm(slug.trim());
      if (!res.ok) {
        toast.error(res.error || "Failed to create the project channel");
        return;
      }
      toast.success(
        res.alreadyExisted ? "This deal already has a project channel" : "Project channel created",
      );
      onClose();
    } finally {
      setBusy(false);
    }
  }, [data, slug, onClose]);

  return (
    <UnifiedModal
      modalId={MODAL_IDS.deals.makeProjectChannel}
      title="Make Project Channel"
      description={
        data?.dealTitle
          ? `Spin up a dedicated Slack channel for "${data.dealTitle}".`
          : "Spin up a dedicated Slack channel for this project."
      }
      size="sm"
    >
      <div className="space-y-4 p-4">
        <p className="text-sm text-muted-foreground">
          The customer chats with the project team from their Hub portal; their messages land in
          the new channel. Owners are invited automatically.
        </p>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="make-project-channel-slug">
            Project slug
          </label>
          <input
            id="make-project-channel-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="e.g. website-rebuild"
            className="w-full rounded-lg border bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--primary)]"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" type="button" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={busy}>
            {busy ? "Creating…" : "Create channel"}
          </Button>
        </div>
      </div>
    </UnifiedModal>
  );
}
